import type { Intent, QualityLevel, TPlayer } from '@setplex/pureya'
import type { VariantOf } from 'variant'

import {
  Events,
  getPlaybackSessionId,
  Intent as PlayerIntent,
  TRACK_MODE,
} from '@setplex/pureya'
import { match, nil, partial, payload, variant } from 'variant'
import { runtime } from '~/app/environment'
import { logger, PlayStartReason, PlayStopReason } from './index.h'
import { PlayerWatcher } from './player-watcher'

export class ViewportObserver {
  config: IntersectionObserverInit = {
    threshold: 0,
    // https://developer.mozilla.org/en-US/docs/Web/API/IntersectionObserver/IntersectionObserver#trackvisibility
  }

  callbacks = new WeakMap()
  observer: IntersectionObserver

  constructor(options: IntersectionObserverInit = {}) {
    this.config = { ...this.config, ...options }
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const callback = this.callbacks.get(entry.target)
        if (callback) {
          callback(entry.isIntersecting)
        }
      })
    }, this.config)
  }

  observe(element: Element, callback: (isVisible: boolean) => void) {
    this.callbacks.set(element, callback)
    this.observer.observe(element)
    return () => {
      this.observer.unobserve(element)
      this.callbacks.delete(element)
    }
  }

  disconnect() {
    this.observer.disconnect()
    this.callbacks = new WeakMap()
  }
}

/**
 * Multiple players elements' visibility tracking using IntersectionObserver
 */
const observer = new ViewportObserver({ threshold: 0.1 })

//
// player actions
//

export type ActionName = keyof typeof Action
export type Action = VariantOf<typeof Action>
export const Action = variant({
  Play: payload<string>(),
  ChangeAudio: payload<string | undefined>(),
  ChangeQuality: payload<number | undefined>(),
  ToggleCast: nil,
  TogglePip: nil,
})
const { Play, ChangeAudio, ChangeQuality, ToggleCast, TogglePip } = Action

//
// player statuses and transitions
//

export type StatusName = keyof typeof Status
export type Status = VariantOf<typeof Status>
export const Status = variant({
  Idle: nil,
  Loading: nil,
  Playing: nil,
  Buffering: nil,
  Paused: nil,
  Seeking: nil,
  Ended: nil,
  Error: payload<unknown>(),
  Disposed: nil,
})

// shortcuts for statuses
const {
  Idle,
  Loading,
  Playing,
  Buffering,
  Paused,
  Seeking,
  Ended,
  Error,
  Disposed,
} = Status

/**
 * Class to represent the state of a single player
 * It is self-contained and updates itself based on player events, without relying on external state management
 */
export class PlayerState {
  private unobserve: () => void
  private watcher: PlayerWatcher
  color = getRandomColor()

  createdAt = Date.now()
  mounted = false
  visible = false
  status: Status = Idle()
  statusChangeAt = Date.now()
  src?: string
  drm?: 'widevine' | 'playready' | 'fairplay'
  currentTime = '0'
  contentLength = '0'
  audioTrackLanguage = ''
  subtitlesLanguage = ''
  videoQuality = ''
  playbackSessionId = ''
  httpErrorCode = ''
  contentErrorCode = ''
  quality?: number

  public readonly player: TPlayer
  public readonly playerId: string

  constructor(player: TPlayer, playerId: string = player.id_) {
    this.player = player
    this.playerId = playerId
    this.trace(`💥 created`)
    this.unobserve = observer.observe(player.el_, (isVisible) => {
      if (isVisible) {
        this.trace(`👁️ player is visible`)
        this.visible = true
      } else {
        this.trace(`🙈 player is not visible`)
        this.visible = false
      }
    })

    // player intents/actions/states watcher, which decides
    // when to emit events based on player events and state transitions
    this.watcher = new PlayerWatcher(this)

    // player intents
    player.on(Events.User.Intent, this.intent)

    // player actions
    player.on('play', this.play)
    player.on('timeupdate', this.syncCurrentTime)
    player.on('durationchange', this.syncContentLength)
    player.on('loadedmetadata', this.syncPlayback)
    player.on(Events.Media.TimeUpdate, this.syncCurrentTime)
    player.on(Events.Media.AudioTrackChanged, this.changeAudio)
    player.on(Events.Media.AudioTracksChanged, this.syncAudioTrackLanguage)
    player.on(Events.Media.SubtitleTrackChanged, this.changeSubtitles)
    player.on(Events.Media.SubtitleTracksChanged, this.syncSubtitlesLanguage)
    player.on('texttrackchange', this.syncSubtitlesLanguage)
    player.on(Events.Media.QualityLevelSelected, this.changeQuality)
    player.on(Events.Media.PlaybackSessionId, this.syncPlaybackSessionId)
    player.on('enterpictureinpicture', this.togglePip)
    player.on('leavepictureinpicture', this.togglePip)
    player.on('chromecastConnected', this.onChromecastConnected)
    player.on('chromecastDisconnected', this.onChromecastDisconnected)
    player.on(Events.Player.SkipIntro, this.skipIntro)
    player.on(Events.Player.SkipCredits, this.skipCredits)

    // player state transitions
    player.on(Events.Media.Load, this.load)
    player.on('playerreset', this.playerreset)
    player.on('loadstart', this.loadstart)
    player.on('playing', this.playing)
    player.on('waiting', this.waiting)
    player.on('stalled', this.stalled)
    player.on('pause', this.pause)
    player.on('ended', this.ended)
    player.on('seeking', this.seeking)
    player.on('seeked', this.seeked)
    player.on('error', this.error)
    player.on('dispose', this.dispose)

    this.syncPlayback()
    this.syncAudioTrackLanguage()
    this.syncSubtitlesLanguage()
    this.syncVideoQuality()
    this.syncPlaybackSessionId(undefined, this.tryGetPlaybackSessionId())
  }

  render = (isMounted: boolean) => {
    this.trace(`🎨 player ${isMounted ? 'mounted' : 'unmounted'}`)
    this.mounted = isMounted
  }

  play = () => {
    if (this.src) {
      this.action(Play(this.src), `▶️ play: ${this.src}`)
    }
  }

  private formatSecond(raw?: number): string {
    return raw == null || !isFinite(raw) || raw < 0
      ? '0'
      : String(Math.round(raw))
  }

  private getAudioTrackLanguage(): string {
    const tracks = this.player.audioTracks()
    if (tracks) {
      for (let i = 0; i < tracks.length; i++) {
        const track = tracks[i]
        if (track.enabled) {
          return track.language || track.label || ''
        }
      }
    }
    return ''
  }

  syncAudioTrackLanguage = () => {
    this.audioTrackLanguage = this.getAudioTrackLanguage()
  }

  changeAudio = (_: unknown) => {
    const nextAudioTrackLanguage = this.getAudioTrackLanguage()
    this.action(
      ChangeAudio(nextAudioTrackLanguage || undefined),
      `🔊 change audio: ${nextAudioTrackLanguage || '-'}`
    )
    this.audioTrackLanguage = nextAudioTrackLanguage
  }

  private syncVideoQuality = () => {
    const quality = this.getVideoQuality()
    this.videoQuality = quality == null ? '' : String(quality)
  }

  private getVideoQuality(): number | undefined {
    if (this.quality != null && this.quality > 0) return this.quality

    const width = Number((this.player as any).videoWidth?.())
    const height = Number((this.player as any).videoHeight?.())
    if (
      Number.isFinite(width) &&
      Number.isFinite(height) &&
      width > 0 &&
      height > 0
    ) {
      return Math.min(width, height)
    }
  }

  private isPictureInPictureActive(): boolean {
    try {
      return Boolean(this.player.isInPictureInPicture?.())
    } catch {
      return false
    }
  }

  changeQuality = (_: unknown, data: QualityLevel) => {
    const { width, height } = data
    const selectedQuality = Math.min(width, height)
    const isAutoSelected = !(selectedQuality > 0)
    const nextQuality = isAutoSelected
      ? this.getVideoQuality()
      : selectedQuality
    this.action(
      ChangeQuality(nextQuality),
      `⚡ change quality: ${nextQuality ? nextQuality + 'p' : '-'}`
    )
    this.quality = isAutoSelected ? undefined : selectedQuality
    this.syncVideoQuality()
  }

  togglePip = () => {
    this.action(TogglePip(), `PiP toggle`)
  }

  onChromecastConnected = () => {
    this.action(ToggleCast(), `📡 chromecast connected`)
  }

  onChromecastDisconnected = () => {
    this.action(ToggleCast(), `📡 chromecast disconnected`)
  }

  private getSubtitlesLanguage(): string {
    const tracks = this.player.textTracks()
    if (tracks) {
      let hasTracks = false
      for (let i = 0; i < tracks.length; i++) {
        hasTracks = true
        const track = tracks[i]
        if (track.mode === TRACK_MODE.Showing) {
          return track.language || track.label || ''
        }
      }
      if (hasTracks) return 'OFF'
    }
    return ''
  }

  syncSubtitlesLanguage = () => {
    this.subtitlesLanguage = this.getSubtitlesLanguage()
  }

  changeSubtitles = (_: unknown) => {
    this.syncSubtitlesLanguage()
  }

  syncPlaybackSessionId = (_: unknown, sid?: string) => {
    this.playbackSessionId = sid || ''
  }

  private tryGetPlaybackSessionId(): string | undefined {
    try {
      return getPlaybackSessionId(this.player)
    } catch (ignore) {
      return undefined
    }
  }

  syncCurrentTime = (_: unknown, current?: number) => {
    const next = this.formatSecond(
      typeof current === 'number' ? current : this.player.currentTime()
    )
    // After media teardown (`playerreset` clears `src`), the element reports
    // 0/NaN. Keep the last known position for analytics play_stop(streamError).
    if (!this.src && next === '0' && this.currentTime !== '0') {
      return
    }
    this.currentTime = next
    this.syncVideoQuality()
  }

  syncContentLength = () => {
    const next = this.formatSecond(this.player.duration())
    // Same teardown case as syncCurrentTime — keep last known content length.
    if (!this.src && next === '0' && this.contentLength !== '0') {
      return
    }
    this.contentLength = next
  }

  syncPlayback = () => {
    this.syncCurrentTime(undefined, this.player.currentTime())
    this.syncContentLength()
  }

  load = (_: unknown, payload?: Events.Media.Load) => {
    const src = payload?.src
    if (src) {
      const status = Idle()
      this.transition(status, `🔗 load: ${src}`)
      this.src = src
      this.drm = getDrmName(payload)
      this.httpErrorCode = ''
      this.contentErrorCode = ''
    }
  }

  playerreset = () => {
    const status = Idle()
    this.transition(status, `🔄 playerreset`)
    this.src = undefined
    this.drm = undefined
    this.quality = undefined
    this.httpErrorCode = ''
    this.contentErrorCode = ''
    // Do not syncPlayback(): Html5 tech reload reports currentTime/duration as
    // 0/NaN, which would wipe stopSecond/contentLength needed for the following
    // play_stop(streamError) (e.g. CSL 433).
    this.syncAudioTrackLanguage()
    this.syncSubtitlesLanguage()
    this.syncVideoQuality()
  }

  loadstart = () => {
    this.httpErrorCode = ''
    this.contentErrorCode = ''
    const status = match(
      this.status,
      partial({
        Idle: (_) => Loading(),
        Paused: (_) => Loading(),
        Ended: (_) => Loading(),
        Error: (_) => Loading(),
        // Playing → Loading
        // Buffering → Loading
        // Seeking → Loading
        default: this.unexpected('loadstart'),
      })
    )
    this.transition(status, `⏳ loadstart`)
  }

  playing = () => {
    // Native PiP window controls do not emit user intents.
    // Synthesize a pause-toggle intent for Paused -> Playing transitions in PiP
    // so analytics reason is userPlay instead of bufferFull.
    if (this.status.type === 'Paused' && this.isPictureInPictureActive()) {
      this.watcher.intent(PlayerIntent.TogglePause)
    }

    const status = match(
      this.status,
      partial({
        Loading: (_) => Playing(),
        Buffering: (_) => Playing(),
        Paused: (_) => Playing(),
        Seeking: (_) => Playing(),
        Ended: (_) => Playing(),
        Playing: (_) => _, // allowed no-op
        default: this.unexpected('playing'),
      })
    )
    this.transition(status, `▶️ playing`)
  }

  waiting = () => {
    const status = match(
      this.status,
      partial({
        Playing: (_) => Buffering(),
        Seeking: (_) => Buffering(),
        Loading: (_) => Buffering(),
        Buffering: (_) => _, // allowed no-op
        Paused: (_) => _, // allowed no-op
        default: this.unexpected('waiting'),
      })
    )
    this.transition(status, `⏳ waiting`)
  }

  stalled = () => {
    const status = match(
      this.status,
      partial({
        Playing: (_) => Buffering(),
        default: this.unexpected('stalled'),
      })
    )
    this.transition(status, `⏳ stalled`)
  }

  pause = () => {
    if (!this.player.ended()) {
      // Native PiP window controls do not emit user intents.
      // Synthesize pause-toggle intent so analytics reason is userPause.
      if (this.isPictureInPictureActive()) {
        this.watcher.intent(PlayerIntent.TogglePause)
      }

      const status = match(
        this.status,
        partial({
          Loading: (_) => Paused(),
          Playing: (_) => Paused(),
          Buffering: (_) => Paused(),
          Seeking: (_) => Paused(),
          default: this.unexpected('pause'),
        })
      )
      this.transition(status, `⏸️ pause`)
    }
  }

  ended = () => {
    const status = match(
      this.status,
      partial({
        Playing: (_) => Ended(),
        Buffering: (_) => Ended(),
        Seeking: (_) => Ended(),
        default: this.unexpected('ended'),
      })
    )
    this.transition(status, `🏁 ended`)
  }

  seeking = () => {
    const status = match(
      this.status,
      partial({
        Playing: (_) => Seeking(),
        Buffering: (_) => Seeking(),
        Paused: (_) => Seeking(),
        default: this.unexpected('seeking'),
      })
    )
    this.transition(status, `⏩ seeking`)
  }

  seeked = () => {
    const status = match(
      this.status,
      partial({
        Seeking: (_) => (this.player.paused() ? Paused() : Playing()),
        Buffering: (_) => (this.player.paused() ? Paused() : Playing()),
        Paused: (_) => (this.player.paused() ? _ : Playing()),
        default: this.unexpected('seeked'),
      })
    )
    this.transition(status, `⏪ seeked`)
  }

  error = () => {
    const error = this.player.error()
    this.httpErrorCode = extractHttpErrorCode(error)
    this.contentErrorCode = extractContentErrorCode(error)
    const status = Error(error)
    this.transition(status, `❗ error`)
  }

  dispose = () => {
    const status = Disposed()
    this.transition(status, `🗑️ dispose`)
    this.unobserve()

    // stop watcher from emitting any further events
    this.watcher.off()

    // unsubscribe from video.js events
    this.player.off(Events.User.Intent, this.intent)
    this.player.off(Events.Player.SkipIntro, this.skipIntro)
    this.player.off(Events.Player.SkipCredits, this.skipCredits)
    this.player.off('play', this.play)
    this.player.off('timeupdate', this.syncCurrentTime)
    this.player.off('durationchange', this.syncContentLength)
    this.player.off('loadedmetadata', this.syncPlayback)
    this.player.off(Events.Media.TimeUpdate, this.syncCurrentTime)
    this.player.off(Events.Media.AudioTrackChanged, this.changeAudio)
    this.player.off(
      Events.Media.AudioTracksChanged,
      this.syncAudioTrackLanguage
    )
    this.player.off(Events.Media.SubtitleTrackChanged, this.changeSubtitles)
    this.player.off(
      Events.Media.SubtitleTracksChanged,
      this.syncSubtitlesLanguage
    )
    this.player.off('texttrackchange', this.syncSubtitlesLanguage)
    this.player.off(Events.Media.QualityLevelSelected, this.changeQuality)
    this.player.off(Events.Media.PlaybackSessionId, this.syncPlaybackSessionId)
    this.player.off('enterpictureinpicture', this.togglePip)
    this.player.off('leavepictureinpicture', this.togglePip)
    this.player.off('chromecastConnected', this.onChromecastConnected)
    this.player.off('chromecastDisconnected', this.onChromecastDisconnected)
    this.player.off(Events.Media.Load, this.load)
    this.player.off('playerreset', this.playerreset)
    this.player.off('loadstart', this.loadstart)
    this.player.off('playing', this.playing)
    this.player.off('waiting', this.waiting)
    this.player.off('stalled', this.stalled)
    this.player.off('pause', this.pause)
    this.player.off('ended', this.ended)
    this.player.off('seeking', this.seeking)
    this.player.off('seeked', this.seeked)
    this.player.off('error', this.error)
    this.player.off('dispose', this.dispose)
  }

  private intent = (_: unknown, intent: Intent) => {
    this.watcher.intent(intent)
  }

  private skipIntro = () => {
    this.watcher.skipSegment(
      PlayStartReason.userMoveIntro,
      PlayStopReason.userMoveIntro
    )
  }

  private skipCredits = () => {
    this.watcher.skipSegment(
      PlayStartReason.userMoveCredits,
      PlayStopReason.userMoveCredits
    )
  }

  private action(action: Action, message: string) {
    this.trace(`${message}`)
    this.watcher.action(action)
  }

  private transition(to: Status, message: string) {
    // ignore if status is not actually changing
    if (to === this.status) return

    const now = Date.now()
    const duration = now - this.statusChangeAt
    this.statusChangeAt = now

    this.trace(`${message} (${this.status.type} /${duration}ms/ → ${to.type})`)
    this.watcher.transition(this.status, to, duration, now)
    this.status = to
  }

  private unexpected(event: string) {
    return (current: Status) => {
      this.trace(`⚠️ unexpected "${event}" while in ${current.type} state`)
      return current
    }
  }

  private trace(message: string) {
    logger.trace(
      `%c${this.playerId}%c ${message}`,
      `color:${this.color};`,
      'color:inherit;'
    )
  }
}

function getDrmName(
  payload?: Events.Media.Load
): 'widevine' | 'playready' | 'fairplay' | undefined {
  if (!payload) return
  const keySystems = payload.keySystems
  if (keySystems) {
    if (
      'com.widevine.alpha' in keySystems &&
      runtime.drms.includes('WIDEVINE')
    ) {
      return `widevine`
    } else if (
      'com.microsoft.playready' in keySystems &&
      runtime.drms.includes('PLAYREADY')
    ) {
      return `playready`
    } else if (
      ('com.apple.fps' in keySystems || 'com.apple.fps.1_0' in keySystems) &&
      runtime.drms.includes('FAIRPLAY')
    ) {
      return `fairplay`
    }
  }
}

function extractContentErrorCode(error: unknown): string {
  const value = pick(error, 'code') ?? pick(error, 'errorCode')
  return value == null ? '' : String(value)
}

function extractHttpErrorCode(error: unknown): string {
  const metadata = pick(error, 'metadata')
  const value =
    pick(error, 'httpErrorCode') ??
    pick(error, 'httpStatus') ??
    pick(error, 'statusCode') ??
    pick(error, 'status') ??
    (metadata != null && typeof metadata === 'object'
      ? pick(metadata, 'statusCode')
      : undefined)
  return value == null ? '' : String(value)
}

function pick(error: unknown, key: string): unknown {
  if (error == null || typeof error !== 'object') return undefined
  return (error as Record<string, unknown>)[key]
}

function getRandomColor() {
  const hue = Math.floor(Math.random() * 360) // pick a random hue (0 to 360)
  const saturation = 70 + Math.random() * 30 // high saturation (70% to 100%) prevents "muddy" or gray colors
  const lightness = 50 + Math.random() * 20 // medium lightness (50% to 70%) ensures visibility on dark backgrounds
  return `hsl(${hue},${saturation}%,${lightness}%)`
}
